import type { ProactiveSuggestion } from '@/store/proactive/proactive.mappers';

const REMIND_HOUR = 9;
const MAX_REMIND_DAYS = 30;

const morningOf = (day: Date, plusDays = 0) =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate() + plusDays, REMIND_HOUR);

/** The day the idea is about, as "On … morning" names it. */
const ideaDay = (suggestion: ProactiveSuggestion): { label: string; date: Date } =>
  suggestion.kind === 'holiday'
    ? { label: `On ${suggestion.holiday.name} Morning`, date: suggestion.holiday.date }
    : {
        label: `On ${suggestion.date.toLocaleDateString('en-US', { weekday: 'long' })} morning`,
        date: suggestion.date,
      };

/**
 * When "Remind me later" can come back: tomorrow, in 3 days, in a week, and the
 * morning of the day itself (the holiday, the routine's day) — only the ones
 * still before the idea expires, never in the past, and not two labels for the
 * same day.
 */
export const reminderOptions = (
  suggestion: ProactiveSuggestion,
  now = new Date(),
): { label: string; until: Date }[] => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const candidates = [
    { label: 'Tomorrow Morning', until: morningOf(today, 1) },
    { label: 'In 3 Days', until: morningOf(today, 3) },
    { label: 'In 1 Week', until: morningOf(today, 7) },
    { label: ideaDay(suggestion).label, until: morningOf(ideaDay(suggestion).date) },
  ];
  const latest = now.getTime() + MAX_REMIND_DAYS * 24 * 60 * 60 * 1000;
  const seen = new Set<string>();
  return candidates
    .filter(({ until }) => {
      const t = until.getTime();
      const beforeExpiry = !suggestion.expiresAt || t < suggestion.expiresAt.getTime();
      return t > now.getTime() && t <= latest && beforeExpiry;
    })
    .sort((a, b) => a.until.getTime() - b.until.getTime())
    .filter(({ until }) => {
      const day = until.toDateString();
      if (seen.has(day)) return false;
      seen.add(day);
      return true;
    });
};
